import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  Modal,
  Text,
  Badge,
  ActionIcon,
  Stack,
  Alert,
  Tabs,
  ScrollArea,
  Pagination,
  Select,
  Radio,
} from '@mantine/core';
import { useSupabase } from '../contexts/SupabaseContext';
import { isButtonAllowed } from '../utils/permissions';
import { useWeb3 } from '../contexts/Web3Context';
import {
  IconPlus,
  IconSearch,
  IconLock,
  IconLockOpen,
  IconAlertCircle,
  IconRefresh,
  IconEdit,
} from '@tabler/icons-react';

const AccountManagement = () => {
  // Supabase
  const { loadAccounts, searchAccounts, isConnected: supabaseConnected, error: supabaseError, supabase, myAdmin } = useSupabase();
  // Web3
  const { isConnected, adminLevel, account, contractWrite, isWriteReady } = useWeb3();
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpened, setAddModalOpened] = useState(false);
  const [newAccount, setNewAccount] = useState('');
  const [frozenAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('registered');
  const [currentPage, setCurrentPage] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [totalAddressCount, setTotalAddressCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 15;

  // Edit modal states
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editForm, setEditForm] = useState({
    address: '',
    account_type: '',
    holder_name: '',
    kyc_yn: 'N'
  });

  // 권한 확인
  const hasAccess = true; // isConnected && (adminLevel === 'Owner' || adminLevel === 'Manager'); //우선 오픈

  const loadAccountsData = useCallback(async () => {
    console.log('Starting loadAccountsData...');
    setLoading(true);
    
    try {
      if (!supabaseConnected) {
        console.log('Supabase not connected, skipping account data load');
        setLoading(false);
        return;
      }

      console.log('Supabase를 통해 계정 데이터 로드 중...');
      
      // 검색어가 있으면 검색 API 사용, 없으면 일반 로드 API 사용
      const result = searchTerm.trim() 
        ? await searchAccounts(searchTerm.trim(), currentPage, itemsPerPage)
        : await loadAccounts(currentPage, itemsPerPage);
      
      if (result && result.data) {
        const accountData = result.data.map((account, index) => {
          // 잔고 포맷팅
          const balance = parseFloat(account.balance || 0);
          const preSale = parseFloat(account.pre_sale || 0);
          const availableAmount = balance - preSale;
          
          // 콤마 포맷팅 함수
          const formatNumber = (num) => {
            return num.toLocaleString();
          };
          
          return {
            address: account.address,
            balance: formatNumber(balance),
            lockedAmount: formatNumber(preSale), // pre_sale을 lockedAmount로 표시
            availableAmount: formatNumber(Math.max(0, availableAmount)),
            freeze_yn: account.freeze_yn,
            kyc_yn: account.kyc_yn,
            holder_name: account.holder_name,
            account_type: account.account_type,
            pre_sale: account.pre_sale
          };
        });
        
        setAccounts(accountData);
        setTotalAddressCount(result.count || 0);
        console.log('Supabase 계정 데이터 로드 완료');
      } else {
        console.log('No account data found');
        setAccounts([]);
        setTotalAddressCount(0);
      }
    } catch (error) {
      console.error('Error loading accounts from Supabase:', error);
      setAccounts([]);
      setTotalAddressCount(0);
    } finally {
      setLoading(false);
    }
  }, [supabaseConnected, loadAccounts, searchAccounts, searchTerm, currentPage, itemsPerPage]);

  // 페이지 변경 핸들러: 페이지 상태만 변경 (데이터 로드는 useEffect에서 처리)
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Supabase 연결 시 자동 로딩
  useEffect(() => {
    if (supabaseConnected && accounts.length === 0 && !loading) {
      console.log('Auto-loading accounts from Supabase...');
      loadAccountsData();
    }
  }, [supabaseConnected, loadAccountsData, loading, accounts]);

  // 페이지 변경 시 데이터 로드 (모든 페이지에서 동작)
  useEffect(() => {
    if (supabaseConnected) {
      loadAccountsData();
    }
  }, [currentPage, supabaseConnected, loadAccountsData]);


  const handleFreezeAccount = async (address) => {
    try {
      if (!isConnected) {
        alert('지갑을 먼저 연결해주세요.');
        return;
      }

      if (adminLevel !== 'Owner') {
        alert('Owner 만 가능한 기능입니다');
        return;
      }

      const ok = window.confirm(`지갑 주소: ${address}\n계정을 동결하시겠습니까? 계정 동결시 입금과 출금이 모두 중지됩니다.`);
      if (!ok) {
        return;
      }

      if (!isWriteReady) {
        alert('컨트랙트 또는 계정 정보를 확인할 수 없습니다.');
        return;
      }

      // 가스 추정 및 트랜잭션 실행
      const gas = await contractWrite.methods.freezeAccount(address).estimateGas({ from: account }).catch(() => undefined);
      await contractWrite.methods.freezeAccount(address).send({ from: account, gas });

      // DB 업데이트
      const { error } = await supabase
        .from('accounts')
        .update({ freeze_yn: 'Y', updated_at: new Date().toISOString() })
        .eq('address', address);
      if (error) {
        console.error('DB 업데이트 실패 (freeze):', error);
      }

      alert('계정이 동결되었습니다.');
      await loadAccountsData();
    } catch (error) {
      console.error('Error freezing account:', error);
      alert(`동결 처리 중 오류가 발생했습니다: ${error?.message || error}`);
    }
  };

  // Edit account functions
  const handleEditAccount = (address) => {
    const account = accounts.find(acc => acc.address === address);
    if (account) {
      setEditForm({
        address: account.address,
        account_type: account.account_type || '',
        holder_name: account.holder_name || '',
        kyc_yn: account.kyc_yn || 'N'
      });
      setEditModalOpened(true);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('accounts')
        .update({
          account_type: editForm.account_type,
          holder_name: editForm.holder_name,
          kyc_yn: editForm.kyc_yn,
          updated_at: new Date().toISOString()
        })
        .eq('address', editForm.address);

      if (error) {
        console.error('Error updating account:', error);
        alert('계정 정보 업데이트에 실패했습니다: ' + error.message);
        return;
      }

      console.log('Account updated successfully');
      setEditModalOpened(false);
      setEditForm({
        address: '',
        account_type: '',
        holder_name: '',
        kyc_yn: 'N'
      });
      
      // 데이터 새로고침
      await loadAccountsData();
    } catch (error) {
      console.error('Error saving account edit:', error);
      alert('계정 정보 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditModalOpened(false);
    setEditForm({
      address: '',
      account_type: '',
      holder_name: '',
      kyc_yn: 'N'
    });
  };

  const handleUnfreezeAccount = async (address) => {
    try {
      if (!isConnected) {
        alert('지갑을 먼저 연결해주세요.');
        return;
      }

      if (adminLevel !== 'Owner') {
        alert('Owner 만 가능한 기능입니다');
        return;
      }

      const ok = window.confirm(`지갑 주소: ${address}\n계정 동결을 해제하시겠습니까? 동결 해제 시 입금과 출금이 허용됩니다.`);
      if (!ok) {
        return;
      }

      if (!isWriteReady) {
        alert('컨트랙트 또는 계정 정보를 확인할 수 없습니다.');
        return;
      }

      // 가스 추정 및 트랜잭션 실행
      const gas = await contractWrite.methods.unfreezeAccount(address).estimateGas({ from: account }).catch(() => undefined);
      await contractWrite.methods.unfreezeAccount(address).send({ from: account, gas });

      // DB 업데이트
      const { error } = await supabase
        .from('accounts')
        .update({ freeze_yn: 'N', updated_at: new Date().toISOString() })
        .eq('address', address);
      if (error) {
        console.error('DB 업데이트 실패 (unfreeze):', error);
      }

      alert('계정 동결이 해제되었습니다.');
      await loadAccountsData();
    } catch (error) {
      console.error('Error unfreezing account:', error);
      alert(`해제 처리 중 오류가 발생했습니다: ${error?.message || error}`);
    }
  };

  const filteredFrozenAccounts = (frozenAccounts || []).filter(account =>
    account && account.address && account.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 페이징 계산 (전체 주소 개수 기준)
  const totalPages = Math.ceil(totalAddressCount / itemsPerPage);
  
  // 서버에서 검색된 결과를 직접 사용 (클라이언트 필터링 제거)
  const filteredAccounts = accounts;
  
  // 디버깅 로그
  console.log('Render - accounts length:', accounts.length);
  console.log('Render - filteredAccounts length:', filteredAccounts.length);
  console.log('Render - currentPage:', currentPage, 'totalPages:', totalPages);

  // 검색어가 변경되면 첫 페이지로 이동 (데이터 로드는 currentPage effect에서 처리)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // 권한이 없는 경우 접근 거부 메시지 표시
  if (!hasAccess) {
    return (
      <Container py="md" pl="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md">
          <Title order={3} mb="sm">Access Denied</Title>
          <Text>
            You need to connect your wallet and have Owner or Manager privileges to access this page.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid py="md" pl="xl">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Account List</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Manage all token holder accounts
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size="1rem" />}
          onClick={() => setAddModalOpened(true)}
          color="dark"
          disabled={!isButtonAllowed(myAdmin, 'other')}
        >
          Add Account
        </Button>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3} mb="xs">Token Holders</Title>
            <Text size="sm" c="dimmed">
              Showing {filteredAccounts.length} of {totalAddressCount} accounts
            </Text>
          </div>
          <TextInput
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size="1rem" />}
            style={{ width: 300 }}
          />
        </Group>

        {supabaseError && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md" mb="md">
            Database 연결 오류: {supabaseError}. 데이터를 불러올 수 없습니다.
          </Alert>
        )}

        <Tabs value={activeTab} onTabChange={setActiveTab}>
          {/* <Tabs.List>
            <Tabs.Tab value="registered">Registered Accounts</Tabs.Tab>
            <Tabs.Tab value="frozen">Frozen Accounts</Tabs.Tab>
          </Tabs.List> */}

          <Tabs.Panel value="registered" pt="md">
            {loading ? (
              <Alert icon={<IconRefresh size="1rem" />} color="blue">
                Loading accounts...
              </Alert>
            ) : filteredAccounts.length === 0 ? (
              <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
                {searchTerm ? 
                  'No registered accounts found matching your search.' : 
                  'No registered accounts found.'
                }
              </Alert>
            ) : (
              <>
                <ScrollArea>
                  <Table striped highlightOnHover>
                    <thead>
                      <tr>
                        <th style={{ padding: '12px' }}>Holder</th>
                        <th style={{ padding: '12px' }}>Wallet Address</th>
                        <th style={{ padding: '12px', textAlign: 'right' }}>Balance</th>
                        <th style={{ padding: '12px', textAlign: 'right' }}>Locked Amount</th>
                        <th style={{ padding: '12px', textAlign: 'right' }}>Available Amount</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>KYC Status</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Account Type</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAccounts.map((account, index) => (
                        <tr key={index}>
                          <td style={{ padding: '12px' }}>
                            <Text size="sm" fw={500}>
                              {account.holder_name || 'Anonymous'}
                            </Text>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <Text size="sm" ff="monospace">
                              {account.address}
                            </Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <Text size="sm" fw={500}>
                              {account.balance} EDENA
                            </Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <Text size="sm" fw={500}>
                              {account.lockedAmount} EDENA
                            </Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <Text size="sm" fw={500}>
                              {account.availableAmount} EDENA
                            </Text>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Badge 
                              color={account.kyc_yn === 'Y' ? 'dark' : 'red'}
                              size="sm"
                              variant="filled"
                            >
                              {account.kyc_yn === 'Y' ? 'Verified' : 'None'}
                            </Badge>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Badge 
                              color="gray"
                              size="sm"
                              variant="light"
                            >
                              {account.account_type || 'Individual'}
                            </Badge>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Badge 
                              color={account.freeze_yn === 'Y' ? 'red' : 'dark'}
                              size="sm"
                              variant="filled"
                            >
                              {account.freeze_yn === 'Y' ? 'Frozen' : 'Active'}
                            </Badge>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <Group spacing="xs" justify="center">
                              {/* <ActionIcon
                                color="gray"
                                variant="outline"
                                size="sm"
                              >
                                <IconEye size="1rem" />
                              </ActionIcon> */}
                              <ActionIcon
                                color="gray"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditAccount(account.address)}
                                disabled={!isButtonAllowed(myAdmin, 'other')}
                              >
                                <IconEdit size="1rem" />
                              </ActionIcon>
                              {account.freeze_yn === 'Y' ? (
                                <ActionIcon
                                  color="green"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnfreezeAccount(account.address)}
                                  disabled={!isButtonAllowed(myAdmin, 'other')}
                                >
                                  <IconLockOpen size="1rem" />
                                </ActionIcon>
                              ) : (
                                <ActionIcon
                                  color="gray"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleFreezeAccount(account.address)}
                                  disabled={!isButtonAllowed(myAdmin, 'other')}
                                >
                                  <IconLock size="1rem" />
                                </ActionIcon>
                              )}
                            </Group>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollArea>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination
                      value={currentPage}
                      onChange={handlePageChange}
                      total={totalPages}
                      size="sm"
                    />
                  </Group>
                )}
              </>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="frozen" pt="md">
            {loading ? (
              <Alert icon={<IconRefresh size="1rem" />} color="blue">
                Loading frozen accounts...
              </Alert>
            ) : filteredFrozenAccounts.length === 0 ? (
              <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
                No frozen accounts found.
              </Alert>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover>
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Frozen Date</th>
                      <th>Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFrozenAccounts.map((account, index) => (
                      <tr key={index}>
                        <td>
                          <Text size="sm" ff="monospace">
                            {account.address}
                          </Text>
                        </td>
                        <td>{account.frozenDate}</td>
                        <td>{account.reason}</td>
                        <td>
                          <ActionIcon
                            color="green"
                            variant="light"
                            onClick={() => handleUnfreezeAccount(account.address)}
                          >
                            <IconLockOpen size="1rem" />
                          </ActionIcon>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            )}
          </Tabs.Panel>
        </Tabs>
      </Card>

      <Modal
        opened={addModalOpened}
        onClose={() => setAddModalOpened(false)}
        title="Add New Account"
      >
        <Stack>
          <TextInput
            label="Account Address"
            placeholder="0x..."
            value={newAccount}
            onChange={(e) => setNewAccount(e.target.value)}
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setAddModalOpened(false)}>
              Cancel
            </Button>
            {/* 임시 비활성화 */}
            {/* <Button onClick={handleAddAccount}>Add Account</Button> */}
          </Group>
        </Stack>
      </Modal>

      {/* Edit Account Modal */}
      <Modal
        opened={editModalOpened}
        onClose={handleCancelEdit}
        title="Edit Account Information"
        size="md"
      >
        <Stack>
          <TextInput
            label="Address"
            value={editForm.address}
            disabled
            description="계정 주소는 변경할 수 없습니다"
          />
          
          <Select
            label="Account Type"
            placeholder="계정 유형을 선택하세요"
            value={editForm.account_type}
            onChange={(value) => handleEditFormChange('account_type', value)}
            data={[
              { value: 'owner', label: 'Owner' },
              { value: 'admin', label: 'Admin' },
              { value: 'exchange', label: 'Exchange' },
              { value: 'foundation', label: 'Foundation' },
              { value: 'individual', label: 'Individual' },
              { value: 'holder', label: 'Holder' }
            ]}
            required
          />

          <TextInput
            label="Holder Name"
            placeholder="보유자 이름을 입력하세요"
            value={editForm.holder_name}
            onChange={(e) => handleEditFormChange('holder_name', e.target.value)}
            required
          />

          <Radio.Group
            label="KYC Status"
            value={editForm.kyc_yn}
            onChange={(value) => handleEditFormChange('kyc_yn', value)}
            required
          >
            <Stack mt="xs">
              <Radio value="Y" label="KYC 완료" />
              <Radio value="N" label="KYC 미완료" />
            </Stack>
          </Radio.Group>

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} loading={loading}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default AccountManagement;
